import { TAU, type Complex } from "./complex";

export type FourierCoefficient = Readonly<
  Complex & { frequency: number; amplitude: number; phase: number }
>;

/** Forward DFT: c[k] = 1/N sum(z[n] exp(-i 2πkn/N)). */
export const dft = (points: readonly Complex[]): FourierCoefficient[] => {
  const n = points.length;
  if (!n || !points.every((p) => Number.isFinite(p.x) && Number.isFinite(p.y)))
    throw new Error("DFT requires finite samples.");
  return Array.from({ length: n }, (_, k) => {
    const frequency = k <= Math.floor((n - 1) / 2) ? k : k - n;
    let x = 0;
    let y = 0;
    for (let j = 0; j < n; j++) {
      const angle = (TAU * frequency * j) / n;
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      x += points[j].x * cos + points[j].y * sin;
      y += points[j].y * cos - points[j].x * sin;
    }
    x /= n;
    y /= n;
    return {
      x,
      y,
      frequency,
      amplitude: Math.hypot(x, y),
      phase: Math.atan2(y, x),
    };
  });
};

export const selectCoefficients = (
  coefficients: readonly FourierCoefficient[],
  count: number,
) => {
  const rotating = coefficients.filter((c) => c.frequency !== 0);
  if (!Number.isInteger(count) || count < 1 || count > rotating.length)
    throw new Error("Invalid circle count.");
  return rotating
    .sort((a, b) => b.amplitude - a.amplitude || a.frequency - b.frequency)
    .slice(0, count);
};

export const reconstruct = (
  coefficients: readonly FourierCoefficient[],
  t: number,
): Complex => {
  let x = 0;
  let y = 0;
  for (const c of coefficients) {
    const angle = TAU * c.frequency * t + c.phase;
    x += c.amplitude * Math.cos(angle);
    y += c.amplitude * Math.sin(angle);
  }
  return { x, y };
};
